import Vue from 'vue';
import { MutationTree } from 'vuex';
import { ProjectModule } from './types';
import { crmapi, ProjectStatus, PossibleUpdates } from '@/types/crm-types';
import '@/plugins/string-extensions';
import { DayInfo } from '@/models/task-day';
import { DisplayOptions, Task, TaskDay, TotalHoursTask, Employee, ProjectDay } from '@/types/store-types';

export const mutations: MutationTree<ProjectModule> = {

  clearState(state) {
    state.projectData = {
      projectId: '',
      projectName: '',
      customer: '',
      owner: '',
      actualHours: 0,
      startDate: new Date(),
      status: ProjectStatus.Completed,
      isClosed: true,
      url: '',
    };
    state.categories = [];
    state.tasks = [];
    state.weeks = [];
    state.days = [];
    state.selectedTasks = [];
    state.holidays = [];
    state.employees = [];
    state.employeeList = [];
  },

  saveDisplayOptions(state, options: DisplayOptions) {
    state.displayOptions = options;
    localStorage.displayOptions = JSON.stringify(options);
  },

  projectLoaded(state, payload: crmapi.ProjectData) {
    state.selectedTasks = [];

    state.projectData = {
      projectId: payload.project.projectId,
      projectName: payload.project.projectName,
      customer: payload.project.customer,
      owner: payload.project.owner,
      startDate: payload.project.startDate.toDate(),
      actualHours: payload.project.actualHours,
      endDate: (payload.project.endDate && payload.project.endDate.toDate()) || undefined,
      status: payload.project.status,
      isClosed: payload.project.status === ProjectStatus.Completed,
      url: payload.project.url,
    };

    state.holidays = payload.holidays.map((d) => d.toDate());

    state.categories = payload.categories.map((p) => {
      return {
        name: p,
        key: p.hashCode(),
      };
    });

    const tasks: Task[] = payload.tasks.map((t) => {
      return {
        taskId: t.taskId,
        name: t.name,
        categoryKey: t.category.hashCode(),
        category: t.category,
        startDate: t.startDate.toDate(),
        endDate: (t.endDate && t.endDate.toDate()) || undefined,
        employeeId: t.employeeId,
        plannedHours: t.plannedHours,
        actualHours: t.actualHours,
        tariff: t.tariff,
        possibleActions: t.possibleActions,
        newTask: false,
      };
    });

    state.categories.forEach((c) => {
      tasks.push(newTask(c.key, payload.weeks.length ? payload.weeks[0].firstDay.toDate() : new Date()));
    });
    state.tasks = tasks;

    state.days = payload.days.map((d) => {
      return {
        taskId: d.taskId,
        taskDayId: d.taskDayId,
        day: d.day.toDate(),
        plannedHours: d.plannedHours,
        actualHours: d.actualHours,
        available: d.available,
      };
    });

    state.weeks = payload.weeks.map((w) => {
      return {
        code: w.code,
        weekNumber: w.weekNumber,
        firstDay: w.firstDay.toDate(),
        closed: false,
        days: weekdays(w, state.holidays),
      };
    });

    state.employees = payload.employees.map((e) => {
      return {
        employeeId: e.employeeId,
        name: e.name,
        startDate: (e.startDate && e.startDate.toDate()) || undefined,
        endDate: (e.endDate && e.endDate.toDate()) || undefined,
        unplannable: (e.um && e.um.map((u) => u.toDate())) || [],
      };
    });
  },

  loadAdditionalProjectData(state, payload: crmapi.ProjectData) {
    state.holidays = state.holidays.concat(payload.holidays.map((d) => d.toDate()));

    const weeks = payload.weeks.map((w) => {
      return {
        code: w.code,
        weekNumber: w.weekNumber,
        firstDay: w.firstDay.toDate(),
        closed: false,
        days: weekdays(w, state.holidays),
      };
    });
    state.weeks = state.weeks.concat(weeks);

    const days = payload.days.map((d) => {
      return {
        taskId: d.taskId,
        taskDayId: d.taskDayId,
        day: d.day.toDate(),
        plannedHours: d.plannedHours,
        actualHours: d.actualHours,
        available: d.available,
      };
    });
    state.days = state.days.concat(days);

    payload.employees.forEach((e) => {
      const employeeIndex = state.employees.findIndex((emp) => emp.employeeId === e.employeeId);
      if (employeeIndex < 0) {
        state.employees.push({
          employeeId: e.employeeId,
          name: e.name,
          startDate: (e.startDate && e.startDate.toDate()) || undefined,
          endDate: (e.endDate && e.endDate.toDate()) || undefined,
          unplannable: e.um.map((u) => u.toDate()),
        });
      } else {
        if (e.um && e.um.length) {
          const emp = state.employees[employeeIndex];
          emp.unplannable = emp.unplannable.concat(e.um.map((u) => u.toDate()));
          Vue.set(state.employees, employeeIndex, emp); // Vue doesn't detect updates in an array of objects, so use Vue.set
        }
      }
    });
  },

  employeesLoaded(state, payload: crmapi.Employee[]) {
    state.employeeList = payload.map((e) => {
      return {
        employeeId: e.employeeId,
        name: e.name,
        startDate: (e.startDate && e.startDate.toDate()) || undefined,
        endDate: (e.endDate && e.endDate.toDate()) || undefined,
        unplannable: e.um.map((u) => u.toDate()),
      };
    });
  },

  ensureEmployee(state, employee: crmapi.Employee) {
    const storeEmp: Employee = {
      employeeId: employee.employeeId,
      name: employee.name,
      startDate: (employee.startDate && employee.startDate.toDate()) || undefined,
      endDate: (employee.endDate && employee.endDate.toDate()) || undefined,
      unplannable: employee.um.map((u) => u.toDate()),
    };

    const index = state.employees.findIndex((e) => e.employeeId === employee.employeeId);
    if (index < 0) {
      state.employees.push(storeEmp);
    } else {
      Vue.set(state.employees, index, storeEmp); // Vue doesn't detect updates in an array of objects, so use Vue.set
    }
  },

  addCategory(state, categoryName: string) {
    const categoryKey = categoryName.hashCode();
    if (state.categories.find((c) => c.key === categoryKey) !== undefined) {
      return;
    }

    state.categories.push({
      name: categoryName,
      key: categoryKey,
    });

    state.tasks.push(newTask(categoryKey, state.weeks.length ? state.weeks[0].firstDay : new Date()));
  },

  closeAllWeeks(state, close: boolean) {
    state.weeks.forEach((w) => { w.closed = close; });
  },

  closeWeek(state, day: Date) {
    const week = state.weeks.find((w) => w.firstDay === day);
    if (week !== undefined) {
      week.closed = !week.closed;
    }
  },

  selectTask(state, id: string) {
    if (state.selectedTasks.find((s) => s === id) === undefined) {
      state.selectedTasks.push(id);
    } else {
      state.selectedTasks = state.selectedTasks.filter((s) => s !== id);
    }
  },

  createTask(state, task: crmapi.Task) {
    const categoryKey = (!task.categoryKey ? task.category.hashCode() : task.categoryKey);
    const storeTask: Task = {
      taskId: task.taskId,
      categoryKey,
      category: task.category,
      name: task.name,
      startDate: task.startDate.toDate(),
      endDate: (task.endDate && task.endDate.toDate()) || undefined,
      employeeId: task.employeeId,
      plannedHours: task.plannedHours,
      tariff: task.tariff,
      possibleActions: task.possibleActions,
      newTask: false,
    };

    state.tasks = state.tasks.filter((t) => t.categoryKey !== categoryKey || (t.categoryKey === categoryKey && t.newTask === false));
    state.tasks.push(storeTask);
    state.tasks.push(newTask(categoryKey, state.weeks.length ? state.weeks[0].firstDay : new Date()));
  },

  updateTask(state, task: crmapi.Task) {
    const categoryKey = (!task.categoryKey ? task.category.hashCode() : task.categoryKey);
    const storeTask: Task = {
      taskId: task.taskId,
      categoryKey,
      category: task.category,
      name: task.name,
      startDate: task.startDate.toDate(),
      endDate: (task.endDate && task.endDate.toDate()) || undefined,
      employeeId: task.employeeId,
      plannedHours: task.plannedHours,
      tariff: task.tariff,
      possibleActions: task.possibleActions,
      newTask: false,
    };

    const index = state.tasks.findIndex((t) => t.taskId === task.taskId);
    Vue.set(state.tasks, index, storeTask); // Vue doesn't detect updates in an array of objects, so use Vue.set
  },

  deleteTask(state, id: string) {
    state.selectedTasks = state.selectedTasks.filter((s) => s !== id);
    state.tasks = state.tasks.filter((t) => t.taskId !== id);
  },

  updateDay(state, payload: crmapi.Day) {
    const index = state.days.findIndex((d) => d.taskDayId === payload.taskDayId);
    const storeDay: TaskDay = {
      taskId: payload.taskId,
      taskDayId: payload.taskDayId,
      day: payload.day.toDate(),
      plannedHours: payload.plannedHours,
      available: payload.available,
    };

    if (index === -1) {
      state.days.push(storeDay);
    } else {
      Vue.set(state.days, index, storeDay); // Vue doesn't detect updates in an array of objects, so use Vue.set
    }
  },

  deleteDay(state, taskDayId: string) {
    const index = state.days.findIndex((d) => d.taskDayId === taskDayId);
    if (index !== -1) {
      state.days.splice(index, 1);
    }
  },

  updateTaskPlannedHours(state, totalHoursTask: TotalHoursTask) {
    const index = state.tasks.findIndex((t) => t.taskId === totalHoursTask.taskId);
    if (index !== -1) {
      const task = state.tasks[index];
      task.plannedHours = totalHoursTask.totalHours;
      Vue.set(state.tasks, index, task); // Vue doesn't detect updates in an array of objects, so use Vue.set
    }
  },
};

const weekdays = (week: crmapi.Week, holidays: Date[]): ProjectDay[] => {
  const d = new Date(week.firstDay);
  const days: ProjectDay[] = [];
  for (let index = 0; index < 7; index++) {
    const day = new Date(d.getFullYear(), d.getMonth(), (d.getDate() + index));
    days.push({
      day,
      isHoliday: holidays.findIndex((h) => h.getTime() === day.getTime()) !== -1,
    });
  }

  return days;
};

const newTask = (key: string, startDate: Date): Task => {
  return {
    taskId: '00000000-0000-0000-0000-000000000000',
    category: '',
    categoryKey: key,
    name: '',
    startDate,
    employeeId: '',
    plannedHours: 0,
    tariff: 0,
    possibleActions: PossibleUpdates.Owner | PossibleUpdates.StartDate | PossibleUpdates.EndDate | PossibleUpdates.Tariff,
    newTask: true,
  };
};
